import { useApolloClient } from '@apollo/client';
import {
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Stack,
    useRadioGroup,
    Text,
    RadioGroup,
    Spinner,
    Radio,
    useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import ZaraButton from 'components/custom-components/buttons/Button';
import Input from 'components/custom-components/Inputs/Input';
import { useError } from 'error-provider/ErrorProvider';
import { PhoneNumberType, useBuyTelephoneNumberMutation, useGetPhoneNumbersLazyQuery } from 'graphql/generated';
import { PhoneIcon } from 'lucide-react';
import RadioCard from 'pages/create-agent/phone-pick/RadioCard';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PhoneStatusType } from 'types';
import { digitPattern } from 'utils';
import { PHONE_TYPE } from 'utils/constants';
import { z } from 'zod';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    agentId: string;
}

export const schema = z.object({
    phoneNumber: z.string().min(1, { message: 'Phone number is required.' }),
});

const options = [
    { label: PHONE_TYPE.LOCAL, icon: <PhoneIcon className='mb-3 h-6 w-6' /> },
    // { label: PHONE_TYPE.TOLL_FREE, icon: <GlobeIcon className='mb-3 h-6 w-6' /> },
];

type Schema = z.infer<typeof schema>;

const BuyPhoneNumberModal = ({ isOpen, onClose, agentId }: Props) => {
    const {
        control,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            phoneNumber: '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });
    const { showError } = useError();
    const toast = useToast();
    const apolloClient = useApolloClient();

    const { getRadioProps } = useRadioGroup({
        defaultValue: PHONE_TYPE.LOCAL,
        onChange: (phone) => {
            setPhoneType(phone);
            setInputValue('');
            setValue('phoneNumber', '');
            setPhoneNumbers([]);
        },
    });

    const [fetchPhones, { loading }] = useGetPhoneNumbersLazyQuery({
        onError(error) {
            showError(undefined, error);
        },
    });

    const [buyTelephoneNumber, { loading: isLoadingBuyTelephoneNumber }] = useBuyTelephoneNumberMutation({
        onError(error) {
            showError(undefined, error);
        },
    });

    const [phoneType, setPhoneType] = useState(PHONE_TYPE.LOCAL);
    const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberType[]>([]);
    const [inputValue, setInputValue] = useState('');

    const inputChangeHandler = (event: any) => {
        const { value } = event.target;
        if (digitPattern.test(value)) {
            setInputValue(value);
            if (value.length === 3) {
                fetchPhones({
                    fetchPolicy: 'network-only',
                    variables: {
                        countryCode: 'GB',
                        numberType: 'local',
                        limit: 10,
                        cityCode: Number(value),
                    },
                    onCompleted(data) {
                        if (data.getPhoneNumbers.phoneNumbers) {
                            setPhoneNumbers(data.getPhoneNumbers.phoneNumbers);
                        }
                    },
                });
            }
        }
    };

    // const onGenerateTollFreeNumbers = () => {
    //     fetchPhones({
    //         variables: {
    //             countryCode: 'GB',
    //             numberType: 'toll_free',
    //             limit: 10,
    //         },
    //         onCompleted(data) {
    //             if (data.getPhoneNumbers.phoneNumbers) {
    //                 setPhoneNumbers(data.getPhoneNumbers.phoneNumbers);
    //             }
    //         },
    //     });
    // };

    const onSubmit = (data: Schema) => {
        buyTelephoneNumber({
            variables: { agentId, phoneNumber: data.phoneNumber },
            onCompleted(response) {
                if (response.buyTelephoneNumber.__typename === 'SuccessType') {
                    apolloClient.cache.modify({
                        id: `AgentType:${agentId}`,
                        fields: {
                            phoneNumber() {
                                return data.phoneNumber;
                            },
                            businessAddress() {
                                return {
                                    bundleStatus: PhoneStatusType.twilio_approved,
                                };
                            },
                        },
                    });

                    onClose();

                    toast({ title: response.buyTelephoneNumber.message, status: 'success' });
                }

                if (response.buyTelephoneNumber.__typename === 'ErrorType') {
                    toast({ title: response.buyTelephoneNumber.message, status: 'error' });
                }
            },
        });
    };

    const phoneNumbersField = useCallback(
        (label: string) => {
            if (!loading && inputValue.length >= 3 && phoneNumbers.length === 0) {
                return <Text>No available phone numbers for that area code</Text>;
            }
            if (phoneNumbers.length === 0) {
                return null;
            }
            return (
                <Controller
                    control={control}
                    name='phoneNumber'
                    render={({ field: { onChange, value } }) => (
                        <>
                            <Text fontSize='sm' fontWeight='bold'>
                                {label}
                            </Text>
                            <RadioGroup value={value} onChange={onChange}>
                                <Stack spacing='1' maxH={500} overflow='auto'>
                                    {phoneNumbers.map((number) => (
                                        <Radio key={number.phoneNumber} value={number.phoneNumber}>
                                            {number.phoneNumber}
                                        </Radio>
                                    ))}
                                </Stack>
                            </RadioGroup>
                        </>
                    )}
                />
            );
        },
        [loading, control, phoneNumbers, inputValue.length]
    );

    useEffect(() => {
        if (errors.phoneNumber?.message) {
            toast({ status: 'error', title: errors.phoneNumber.message });
        }
    }, [errors, toast]);

    return (
        <Modal isCentered isOpen={isOpen} onClose={onClose} motionPreset='slideInBottom' size='xl'>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader className='tracking-tight font-bold text-[#050536]' fontSize='2xl'>
                    Choose a phone number
                </ModalHeader>
                <ModalBody>
                    <Stack as='form' onSubmit={handleSubmit(onSubmit)} spacing='3'>
                        <Stack direction='row' flexWrap='wrap'>
                            {options.map(({ label, icon }) => {
                                const radio = getRadioProps({ value: label });
                                return (
                                    <RadioCard key={label} {...radio}>
                                        {icon}
                                        <Text fontSize='sm' fontWeight='bold'>
                                            {' '}
                                            {label}
                                        </Text>
                                    </RadioCard>
                                );
                            })}
                        </Stack>

                        {phoneType === PHONE_TYPE.LOCAL && (
                            <Stack>
                                <Input
                                    label='Enter Area Code'
                                    rightIcon={
                                        loading &&
                                        phoneType === PHONE_TYPE.LOCAL &&
                                        inputValue.length && <Spinner color='#3182ce' size='xs' />
                                    }
                                    value={inputValue}
                                    onChange={inputChangeHandler}
                                    placeholder='Enter 3 digit area code'
                                    error={undefined}
                                    leftIcon={undefined}
                                    groupWidth={undefined}
                                    link={undefined}
                                    inputCursor={undefined}
                                    inputHover={undefined}
                                    iconHandler={undefined}
                                    inputProps={undefined}
                                    isSignUp={undefined}
                                    inputStyles={undefined}
                                />

                                {phoneNumbersField('Select a local number:')}
                            </Stack>
                        )}

                        {/* {phoneType === PHONE_TYPE.TOLL_FREE && (
                            <Stack>
                                <ZaraButton
                                    variant='blackAlpha'
                                    fontSize='sm'
                                    isLoading={loading}
                                    onClick={onGenerateTollFreeNumbers}
                                >
                                    Generate Toll-Free Numbers
                                </ZaraButton>

                                {phoneNumbersField('Select a toll-free number:')}
                            </Stack>
                        )} */}
                        <Flex justifyContent='space-between'>
                            <Button variant='outline' fontSize='sm' onClick={onClose}>
                                Back
                            </Button>
                            <ZaraButton
                                variant='blackAlpha'
                                fontSize='sm'
                                type='submit'
                                isLoading={isLoadingBuyTelephoneNumber}
                            >
                                Save
                            </ZaraButton>
                        </Flex>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default BuyPhoneNumberModal;
